import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, Check, Loader2, Leaf, Users, Compass, ShieldCheck } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { API_BASE } from "@/lib/api";
import { IMG } from "@/data/platform";

const TEAM_SIZES = ["5–10", "10–25", "25–50", "50+"];
const DURATIONS = ["2–3 days", "5 days", "7 days"];
const MONTHS = ["Flexible", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const BUDGETS = ["₹25–50k / person", "₹50k–1L / person", "₹1L+ / person", "Flexible"];
const GOALS = ["Leadership reset", "Team bonding", "Burnout recovery", "Fitness kickstart", "Strategy off-site", "Reward & recognition"];
const FORMATS = [
  { icon: Leaf, name: "The 3-Day Recharge", desc: "A long-weekend off-site: morning movement, facilitated sessions, spa evenings and zero logistics for your team." },
  { icon: Users, name: "The 5-Day Leadership Reset", desc: "Deep work meets deep rest — strategy blocks woven between yoga, treks and Ayurvedic recovery." },
  { icon: Compass, name: "The 7-Day Transformation", desc: "The full Executive Reset: measurable wellness programming, adventure days and a coffee-estate finale." },
];

const chip = (on) => `rounded-full px-4 py-2.5 text-sm border transition-colors ${on ? "bg-[#2D372B] border-[#2D372B] text-[#F7F5F0]" : "border-[#E5E2D9] bg-white text-[#5C665A] hover:border-[#89937C]"}`;
const inputCls = "w-full h-12 rounded-xl bg-white border border-[#E5E2D9] px-4 focus:outline-none focus:border-[#89937C]";

export default function CorporateRetreats() {
  const [form, setForm] = useState({ company: "", name: "", email: "", phone: "", team_size: "10–25", duration: "2–3 days", month: "Flexible", budget_per_person: null, goals: [], destination_pref: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleGoal = (g) => setForm((f) => ({ ...f, goals: f.goals.includes(g) ? f.goals.filter((x) => x !== g) : [...f.goals, g] }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API_BASE}/retreats/enquire`, { ...form, phone: form.phone || null, destination_pref: form.destination_pref || null, notes: form.notes || null });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the enquiry — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <Nav />
      <section className="relative h-[56vh] min-h-[400px] flex items-end overflow-hidden">
        <img src={IMG.kerala} alt="Corporate wellness retreat" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A211A]/90 via-[#1A211A]/30 to-transparent" />
        <div className="relative max-w-7xl mx-auto w-full px-6 md:px-12 pb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-[#C9CFC0] font-semibold mb-3">The Executive Reset Collection</div>
          <h1 data-testid="retreats-title" className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#F7F5F0] tracking-tight max-w-2xl leading-[1.05]">
            Corporate retreats your team will <span className="italic text-[#E8C9B8]">actually thank you for.</span>
          </h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        {done ? (
          <div data-testid="retreats-success" className="max-w-xl mx-auto text-center py-16">
            <div className="w-16 h-16 rounded-full bg-[#89937C]/15 flex items-center justify-center mx-auto mb-6"><Check size={26} className="text-[#89937C]" /></div>
            <h2 className="font-serif-display text-3xl sm:text-4xl">Enquiry received.</h2>
            <p className="text-[#5C665A] mt-4 leading-relaxed">Coach Sanjay will come back to <span className="font-medium text-[#2D372B]">{form.email}</span> within 24 hours with a tailored retreat proposal for {form.company}.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <p className="text-lg text-[#5C665A] leading-relaxed">Off-sites where the agenda includes sunrise movement, real recovery and India's most restorative places — planned door-to-door, facilitated by Coach Sanjay, with strategy time protected.</p>
              <div className="mt-8 space-y-4">
                {FORMATS.map((f) => (
                  <div key={f.name} className="flex items-start gap-4 bg-white border border-[#E5E2D9] rounded-3xl p-6">
                    <div className="w-11 h-11 rounded-2xl bg-[#89937C]/10 flex items-center justify-center shrink-0"><f.icon size={18} className="text-[#89937C]" /></div>
                    <div>
                      <h3 className="font-serif-display text-xl">{f.name}</h3>
                      <p className="text-sm text-[#5C665A] mt-1 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center gap-2.5 text-sm text-[#5C665A]">
                <ShieldCheck size={16} className="text-[#AD6A48]" /> End-to-end handled: stays, transfers, facilitation, dietary needs and contingency plans.
              </div>
            </div>

            <form onSubmit={submit} className="bg-white border border-[#E5E2D9] rounded-3xl p-7 sm:p-9 space-y-6 h-fit">
              <div className="flex items-center gap-2"><Briefcase size={17} className="text-[#AD6A48]" /><h2 className="font-serif-display text-2xl">Plan your retreat</h2></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="retreat-company" required minLength={2} value={form.company} onChange={(e) => set("company")(e.target.value)} placeholder="Company name" className={inputCls} />
                <input data-testid="retreat-name" required minLength={2} value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="Your name" className={inputCls} />
                <input data-testid="retreat-email" required type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} placeholder="Work email" className={inputCls} />
                <input data-testid="retreat-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="Phone / WhatsApp (optional)" className={inputCls} />
              </div>
              <div>
                <div className="overline mb-2.5">Team size</div>
                <div className="flex flex-wrap gap-2">{TEAM_SIZES.map((t) => <button type="button" key={t} data-testid={`retreat-size-${t.replace(/[^0-9+]/g, "")}`} onClick={() => set("team_size")(t)} className={chip(form.team_size === t)}>{t} people</button>)}</div>
              </div>
              <div>
                <div className="overline mb-2.5">Duration</div>
                <div className="flex flex-wrap gap-2">{DURATIONS.map((d) => <button type="button" key={d} data-testid={`retreat-duration-${d.replace(/[^0-9]/g, "")}`} onClick={() => set("duration")(d)} className={chip(form.duration === d)}>{d}</button>)}</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="overline mb-2.5">Preferred month</div>
                  <select data-testid="retreat-month" value={form.month} onChange={(e) => set("month")(e.target.value)} className={inputCls}>
                    {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <div className="overline mb-2.5">Budget per person</div>
                  <select data-testid="retreat-budget" value={form.budget_per_person || ""} onChange={(e) => set("budget_per_person")(e.target.value || null)} className={inputCls}>
                    <option value="">Select (optional)</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <div className="overline mb-2.5">What should this retreat achieve?</div>
                <div className="flex flex-wrap gap-2">{GOALS.map((g) => <button type="button" key={g} data-testid={`retreat-goal-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => toggleGoal(g)} className={chip(form.goals.includes(g))}>{g}</button>)}</div>
              </div>
              <input data-testid="retreat-destination" value={form.destination_pref} onChange={(e) => set("destination_pref")(e.target.value)} placeholder="Destination in mind? (optional — e.g. Kerala, Coorg, Goa)" className={inputCls} />
              <textarea data-testid="retreat-notes" value={form.notes} onChange={(e) => set("notes")(e.target.value)} placeholder="Anything else we should know? (optional)" rows={3} className="w-full rounded-xl bg-white border border-[#E5E2D9] px-4 py-3 focus:outline-none focus:border-[#89937C]" />
              <button data-testid="retreat-submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors disabled:opacity-60">
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Briefcase size={16} />} Request a retreat proposal
              </button>
              <p className="text-xs text-[#5C665A] text-center">Response within 24 hours. No obligation.</p>
            </form>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
